// src/pages/AdminLeads.jsx
// STATUS: active | UPDATED: 2026-09-15
// LOG:
//   2026-09-15  New. Leads, inside the product instead of in a database console.
//
// Everybody who has filled in the form on jandatraining.com.
//
// WHY THIS EXISTS AT ALL
//
// The admin email fired on every lead has always carried one link, and it
// pointed at the Supabase API host with /project/_/editor on the end, which is
// not a page. It 404s. So a lead would land, the email would arrive, and the
// only way to actually read it was the email body itself.
//
// Tyler asked for "View in Pulse or View in the Academy". Pulse is empty on
// purpose, so this is the Academy answer and the email now points here.
//
// NO IP ADDRESS AND NO USER AGENT ON SCREEN. Both are stored by submit-lead for
// abuse triage and neither tells Jazz anything about whether to call somebody
// back. A screen that shows every column it has is a database console with a
// nicer font, which is the thing this replaces.

import { useEffect, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Badge, Spinner, Alert, AlertIcon, Link } from '@chakra-ui/react'
import { Mail, Phone, Building2, Inbox } from 'lucide-react'
import { supabase } from '../lib/supabase'

function when(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const days = Math.floor((Date.now() - d.getTime()) / 86400000)
  const stamp = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  if (days === 0) return `Today, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
  if (days === 1) return 'Yesterday'
  if (days < 30) return `${stamp} • ${days} days ago`
  return stamp
}

function Lead({ lead }) {
  return (
    <Box borderTop="1px solid" borderColor="line" py={6}>
      <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap" mb={2}>
        <Box>
          <Text fontSize="lg" fontWeight={600} letterSpacing="-0.01em">
            {lead.name}
          </Text>
          <HStack spacing={2} color="inkMuted" fontSize="body" mt={0.5}>
            <Building2 size={14} />
            <Text>{lead.dealership}</Text>
            {lead.role && <Text color="inkDim">• {lead.role}</Text>}
          </HStack>
        </Box>
        <VStack align="flex-end" spacing={1}>
          <Text fontSize="sm" color="inkDim">
            {when(lead.created_at)}
          </Text>
          {lead.dealership_type && <Badge variant="subtle">{lead.dealership_type}</Badge>}
        </VStack>
      </Flex>

      {lead.message && (
        <Text fontSize="body" lineHeight={1.7} color="ink" whiteSpace="pre-wrap" mt={3} maxW="measure">
          {lead.message}
        </Text>
      )}

      {/* Tap to reply, because the only thing anybody does on this screen is
          get in touch with the person. */}
      <HStack spacing={5} mt={4} flexWrap="wrap">
        <Link href={`mailto:${lead.email}?subject=${encodeURIComponent('Re: Dealership training inquiry')}`} fontSize="body">
          <HStack spacing={2}>
            <Mail size={14} />
            <Text>{lead.email}</Text>
          </HStack>
        </Link>
        {lead.phone && (
          <Link href={`tel:${String(lead.phone).replace(/[^\d+]/g, '')}`} fontSize="body">
            <HStack spacing={2}>
              <Phone size={14} />
              <Text>{lead.phone}</Text>
            </HStack>
          </Link>
        )}
      </HStack>

      {/* Only when something actually went wrong. A row of green ticks on every
          lead is noise, a single failed confirmation is worth knowing about. */}
      {lead.email_error && (
        <Text fontSize="sm" color="inkDim" mt={3}>
          An email on this one did not send. {lead.email_error}
        </Text>
      )}
    </Box>
  )
}

export default function AdminLeads() {
  const [state, setState] = useState({ loading: true, leads: [], error: null })

  useEffect(() => {
    let alive = true
    supabase
      .from('leads')
      .select('id, created_at, name, dealership, role, dealership_type, email, phone, message, email_error')
      .order('created_at', { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (!alive) return
        setState({ loading: false, leads: data || [], error: error ? error.message : null })
      })
    return () => {
      alive = false
    }
  }, [])

  return (
    <VStack align="stretch" spacing={6} maxW="sheet">
      <Box>
        <Text textStyle="pageEyebrow" mb={1}>
          From jandatraining.com
        </Text>
        <Text textStyle="pageTitle">Leads</Text>
      </Box>

      {state.loading && (
        <HStack spacing={3} color="inkMuted">
          <Spinner size="sm" />
          <Text fontSize="body">Loading</Text>
        </HStack>
      )}

      {state.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {state.error}
        </Alert>
      )}

      {/* An honest empty state. The form works and nobody has used it yet is a
          different fact from something is broken, and the screen should say
          which one it is. */}
      {!state.loading && !state.error && state.leads.length === 0 && (
        <VStack align="flex-start" spacing={2} py={10}>
          <Inbox size={22} />
          <Text fontSize="lg" fontWeight={600}>
            Nothing yet
          </Text>
          <Text fontSize="body" color="inkMuted" maxW="measure">
            Nobody has filled in the form on jandatraining.com. When somebody does they land here, and Jazz
            gets an email at the same time.
          </Text>
        </VStack>
      )}

      {!state.loading && state.leads.length > 0 && (
        <Box>
          <Text fontSize="body" color="inkMuted" mb={2}>
            {state.leads.length === 1 ? 'One person has' : `${state.leads.length} people have`} asked to talk.
          </Text>
          {state.leads.map((lead) => (
            <Lead key={lead.id} lead={lead} />
          ))}
        </Box>
      )}
    </VStack>
  )
}
